import { c as _c } from "react/compiler-runtime";
import { Badge, Box, HStack, Link, Text, VStack } from '@chakra-ui/react';
import { RagSourceHoverCard, typeLabelForSourceType } from '@src/components/sco/MarkdownRenderer/RagSourceHoverPreview';
import { ReactNode } from 'react';
export type ScoCitationProps = {
  'rag-source-id'?: string;
  title?: string;
  url?: string;
  'source-type'?: string;
  'start-time'?: string | number;
  'heading-path'?: string;
  author?: string;
  excerpt?: string;
  'image-url'?: string;
  children?: ReactNode;
};
function formatStartTime(seconds: string | number | undefined): string | null {
  if (seconds == null || seconds === '') {
    return null;
  }
  const n = typeof seconds === 'string' ? parseInt(seconds, 10) : seconds;
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Compact citation card for chat. Always render {children} after the card
 * so self-closing HTML parse traps (rehype-raw swallowing following prose)
 * still surface trailing content when the server emits paired tags.
 *
 * Linked RAG sources show a HoverCard preview (featured image, title, teaser)
 * looked up from CMS by source type / URL.
 */
export const ScoCitation = props => {
  const $ = _c(36);
  const title = props.title || "Source";
  const url = props.url;
  const t0 = props["source-type"];
  let t1;
  if ($[0] !== t0) {
    t1 = typeLabelForSourceType(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const type = t1;
  const t2 = props["start-time"];
  let t3;
  if ($[2] !== t2) {
    t3 = formatStartTime(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const timeLabel = t3;
  const heading = props["heading-path"];
  const excerpt = props.excerpt;
  const imageUrl = props["image-url"];
  const t4 = url ? "pointer" : undefined;
  let t5;
  if ($[4] !== type) {
    t5 = <Badge colorPalette="blue" size="sm" variant="subtle">{type}</Badge>;
    $[4] = type;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== timeLabel) {
    t6 = timeLabel && <Badge colorPalette="gray" size="sm" variant="outline">{timeLabel}</Badge>;
    $[6] = timeLabel;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== title || $[9] !== url) {
    t7 = url ? <Link fontWeight="semibold" href={url} rel="noopener noreferrer" target="_blank">{title}</Link> : <Text fontWeight="semibold">{title}</Text>;
    $[8] = title;
    $[9] = url;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t6 || $[13] !== t7) {
    t8 = <HStack gap={2} wrap="wrap">{t5}{t6}{t7}</HStack>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== heading) {
    t9 = heading && <Text color="fg.muted" fontSize="xs">{heading}</Text>;
    $[15] = heading;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== excerpt) {
    t10 = excerpt && <Text color="fg.muted" fontSize="xs" lineClamp={2}>{excerpt}</Text>;
    $[17] = excerpt;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t8 || $[21] !== t9) {
    t11 = <VStack align="stretch" gap={0.5}>{t8}{t9}{t10}</VStack>;
    $[19] = t10;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t4) {
    t12 = <Box as="span" bg="bg.muted" borderColor="border" borderRadius="md" borderWidth="1px" cursor={t4} display="inline-block" fontSize="sm" maxW="full" mr={1} my={1} px={2} py={1} verticalAlign="middle">{t11}</Box>;
    $[23] = t11;
    $[24] = t4;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const card = t12;
  let t13;
  if ($[26] !== card || $[27] !== excerpt || $[28] !== imageUrl || $[29] !== props || $[30] !== title || $[31] !== url) {
    t13 = url ? <RagSourceHoverCard fallbackImageUrl={imageUrl} fallbackSnippet={excerpt} fallbackTitle={title} href={url} sourceType={props["source-type"]}>{card}</RagSourceHoverCard> : card;
    $[26] = card;
    $[27] = excerpt;
    $[28] = imageUrl;
    $[29] = props;
    $[30] = title;
    $[31] = url;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== props.children || $[34] !== t13) {
    t14 = <>{t13}{props.children}</>;
    $[33] = props.children;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
};
export default ScoCitation;